import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FlaskConical, FileText, User, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuote } from "@/context/QuoteContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/lib/siteContent";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle,
} from "@/components/ui/sheet";

const DEFAULT_NAV = [
  { to: "/urunler", label: "Ürün Kataloğu" },
  { to: "/sektorler", label: "Sektörler" },
  { to: "/kullanim-alanlari", label: "Kullanım Alanları" },
  { to: "/tedarik-sureci", label: "Tedarik Süreci" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/iletisim", label: "İletişim" },
];

export default function Header() {
  const sc = useSiteContent();
  const NAV = sc?.ust_menu?.length ? sc.ust_menu.map((m) => ({ to: m.url, label: m.ad })) : DEFAULT_NAV;
  const { kalemCount: count, setOpen } = useQuote();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Rota değişince mobil menüyü otomatik kapat (karartma/kaydırma kilidi kalksın)
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const go = (to) => { setMenuOpen(false); navigate(to); };

  return (
    <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md text-white border-b border-slate-800">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6">
        <div className="flex items-center h-14 sm:h-16 gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-teal-500 flex items-center justify-center">
              <FlaskConical className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-head font-bold text-base sm:text-lg tracking-tight">MOLAR KİMYA</div>
              <div className="text-[9px] sm:text-[10px] text-teal-300 font-medium -mt-0.5">2005'TEN BU YANA</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "")}`}
                className="px-3 py-2 text-sm text-slate-200 hover:text-white hover:bg-white/10 rounded-md transition-colors">
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 ml-auto">
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}
              data-testid="open-quote-drawer-btn" aria-label="Teklif listem"
              className="relative text-white hover:bg-white/10 px-2 sm:px-3">
              <FileText className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Teklif Listem</span>
              {count > 0 && (
                <Badge data-testid="quote-count-badge"
                  className="absolute -top-0.5 -right-0.5 sm:static sm:ml-1.5 bg-teal-500 text-white hover:bg-teal-500 h-4 min-w-4 sm:h-5 sm:min-w-5 px-1 sm:px-1.5 rounded-full text-[10px]">
                  {count}
                </Badge>
              )}
            </Button>
            {user && user.role ? (
              <Button variant="ghost" size="sm" data-testid="account-btn" aria-label="Hesabım"
                onClick={() => navigate(user.role === "admin" ? "/yonetim" : "/hesabim")}
                className="text-white hover:bg-white/10 px-2 sm:px-3">
                <User className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{user.role === "admin" ? "Yönetim" : "Hesabım"}</span>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" data-testid="login-nav-btn" aria-label="Giriş"
                onClick={() => navigate("/giris")} className="text-white hover:bg-white/10 px-2 sm:px-3">
                <User className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Giriş</span>
              </Button>
            )}

            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10 shrink-0"
                  data-testid="mobile-menu-btn" aria-label="Menü">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-slate-900 text-white border-slate-800 w-[280px] overflow-y-auto">
                <SheetTitle className="text-white font-head mt-2 mb-4">Menü</SheetTitle>
                <nav className="flex flex-col gap-1">
                  {NAV.map((n) => (
                    <button key={n.to} onClick={() => go(n.to)} data-testid={`mobile-nav-${n.to.replace(/\//g, "")}`}
                      className="text-left px-3 py-3 text-base hover:bg-white/10 rounded-md">{n.label}</button>
                  ))}
                  <div className="border-t border-slate-700 my-2" />
                  <button onClick={() => { setMenuOpen(false); setOpen(true); }} data-testid="mobile-nav-quote"
                    className="text-left px-3 py-3 text-base hover:bg-white/10 rounded-md flex items-center justify-between">
                    <span>Teklif Listem</span>
                    {count > 0 && <Badge className="bg-teal-500 text-white">{count}</Badge>}
                  </button>
                  {user && user.role ? (
                    <button onClick={() => go(user.role === "admin" ? "/yonetim" : "/hesabim")} data-testid="mobile-nav-account"
                      className="text-left px-3 py-3 text-base hover:bg-white/10 rounded-md">
                      {user.role === "admin" ? "Yönetim" : "Hesabım"}
                    </button>
                  ) : (
                    <button onClick={() => go("/giris")} data-testid="mobile-nav-login"
                      className="text-left px-3 py-3 text-base hover:bg-white/10 rounded-md">Giriş</button>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
